import * as React from 'react';

import { cn } from '../utils/cn';

const VIEW_W = 96;
const VIEW_H = 36;

export type SparklineProps = {
  /** Series values; scaled to their own min/max. Empty renders a flat baseline. */
  values: number[];
  /** Colour via `currentColor`, e.g. `text-primary`. Size via `w-*`/`h-*`. */
  className?: string;
  /** Draw-in animation; change `animationKey` to replay it. */
  animate?: boolean;
  animationKey?: string;
};

/**
 * Tiny trend line for KPI tiles. Real series only — callers omit it when there
 * is no history rather than passing decorative data.
 */
export function Sparkline({
  values,
  className,
  animate = false,
  animationKey,
}: SparklineProps) {
  const points = React.useMemo(() => {
    const series = values.length > 0 ? values : [0, 0];
    const max = Math.max(...series, 0.001);
    const min = Math.min(...series, 0);
    const span = max - min || 1;
    return series
      .map((v, i) => {
        const x = 2 + (i * (VIEW_W - 4)) / Math.max(series.length - 1, 1);
        const y = VIEW_H - 4 - ((v - min) / span) * (VIEW_H - 8);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }, [values]);
  const flat = values.every((v) => v === 0);

  return (
    <svg
      key={animationKey}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      fill="none"
      aria-hidden
      preserveAspectRatio="none"
      className={cn('h-9 w-24 shrink-0', className)}
    >
      {animate ? (
        <style>{`
          @keyframes ui-sparkline-draw { from { stroke-dashoffset: 1; opacity: .35 } to { stroke-dashoffset: 0; opacity: 1 } }
          @media (prefers-reduced-motion: reduce) { .ui-sparkline-line { animation: none !important } }
        `}</style>
      ) : null}
      <polyline
        points={points}
        stroke="currentColor"
        strokeWidth={2.25}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity={flat ? 0.45 : 1}
        {...(animate
          ? {
              className: 'ui-sparkline-line',
              pathLength: 1,
              style: {
                strokeDasharray: 1,
                strokeDashoffset: 0,
                animation: 'ui-sparkline-draw 520ms ease-out both',
              },
            }
          : {})}
      />
    </svg>
  );
}
